import { useEffect, useState } from 'react';
import { Box, chakra, Flex, Grid, Heading, Input, Text } from '@chakra-ui/react';
import {
  CLAUDE_MODELS,
  defaultNotificationSettings,
  defaultPortalSettings,
  type NotificationSettings,
  type PortalSettings,
  type SiteMode,
} from '@denise-augusta/domain';
import { executeAction } from '@/lattice/runtime';
import { usePortalUser } from '@/auth/roles';

const inputStyles = {
  px: '0.9rem',
  py: '0.6rem',
  bg: 'paper.white',
  border: '1px solid',
  borderColor: 'paper.border',
  fontFamily: 'body',
  fontSize: '0.85rem',
  color: 'ink.body',
  borderRadius: '8px',
  _focus: { borderColor: 'brand.mid', outline: 'none', boxShadow: '0 0 0 1px #3D52A8' },
} as const;

/**
 * Portal settings — admin only. Site access mode and the LLM configuration
 * used by the ✨ Enhance buttons across the portal.
 */
export function Settings() {
  const user = usePortalUser();
  const [mode, setMode] = useState<SiteMode | null>(null);
  const [switching, setSwitching] = useState(false);
  const [settings, setSettings] = useState<PortalSettings>(defaultPortalSettings);
  const [savingLlm, setSavingLlm] = useState(false);
  const [notifications, setNotifications] = useState<NotificationSettings>(defaultNotificationSettings);
  const [recipientsText, setRecipientsText] = useState('');
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [status, setStatus] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    executeAction<{ mode: SiteMode }>('site-content:getSiteMode', {}, user)
      .then((result) => setMode(result.mode))
      .catch((err: Error) => setStatus({ kind: 'error', text: err.message }));
    executeAction<PortalSettings>('portal-settings:getPortalSettings', {}, user)
      .then(setSettings)
      .catch((err: Error) => setStatus({ kind: 'error', text: err.message }));
    executeAction<NotificationSettings>('form-submissions:getNotificationSettings', {}, user)
      .then((loaded) => {
        setNotifications(loaded);
        setRecipientsText(loaded.recipients.join(', '));
      })
      .catch((err: Error) => setStatus({ kind: 'error', text: err.message }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeMode = async (next: SiteMode) => {
    if (next === mode || switching) return;
    setSwitching(true);
    setStatus(null);
    try {
      const result = await executeAction<{ mode: SiteMode }>(
        'site-content:setSiteMode',
        { mode: next },
        user
      );
      setMode(result.mode);
      setStatus({ kind: 'success', text: `Site is now ${result.mode}.` });
    } catch (err) {
      setStatus({ kind: 'error', text: err instanceof Error ? err.message : 'Failed to change site mode' });
    } finally {
      setSwitching(false);
    }
  };

  const saveLlm = async () => {
    setSavingLlm(true);
    setStatus(null);
    try {
      const saved = await executeAction<PortalSettings>(
        'portal-settings:updatePortalSettings',
        { settings },
        user
      );
      setSettings(saved);
      setStatus({ kind: 'success', text: 'LLM settings saved.' });
    } catch (err) {
      setStatus({ kind: 'error', text: err instanceof Error ? err.message : 'Failed to save settings' });
    } finally {
      setSavingLlm(false);
    }
  };

  const notificationsUnreachable =
    notifications.enabled && recipientsText.split(',').every((address) => !address.trim());

  const saveNotifications = async () => {
    setSavingNotifications(true);
    setStatus(null);
    try {
      const saved = await executeAction<NotificationSettings>(
        'form-submissions:updateNotificationSettings',
        {
          settings: {
            ...notifications,
            recipients: recipientsText.split(',').map((address) => address.trim()).filter(Boolean),
          },
        },
        user
      );
      setNotifications(saved);
      setRecipientsText(saved.recipients.join(', '));
      setStatus({ kind: 'success', text: 'Notification settings saved.' });
    } catch (err) {
      setStatus({
        kind: 'error',
        text: err instanceof Error ? err.message : 'Failed to save notification settings',
      });
    } finally {
      setSavingNotifications(false);
    }
  };

  return (
    <Box>
      <Heading fontFamily="heading" fontSize="2.2rem" fontWeight="300" color="brand.ink" mb="0.4rem">
        Settings
      </Heading>
      <Text fontSize="0.9rem" color="ink.soft" mb="2rem">
        Site access and portal configuration. Admin only.
      </Text>

      {status && (
        <Box
          mb="1.5rem"
          px="1.2rem"
          py="0.8rem"
          borderRadius="10px"
          fontSize="0.85rem"
          bg={status.kind === 'success' ? 'brand.wash' : '#FBEDEB'}
          border="1px solid"
          borderColor={status.kind === 'success' ? 'brand.pale' : 'brand.terracotta'}
          color={status.kind === 'success' ? 'brand.ink' : 'brand.terracotta'}
        >
          {status.text}
        </Box>
      )}

      {/* ── Site mode ─────────────────────────────────────────────── */}
      <Flex
        bg="paper.white"
        border="1px solid"
        borderColor={mode === 'live' ? 'brand.pale' : 'brand.terracotta'}
        borderRadius="16px"
        p="1.8rem"
        mb="1.5rem"
        align={{ base: 'flex-start', md: 'center' }}
        justify="space-between"
        direction={{ base: 'column', md: 'row' }}
        gap="1.2rem"
      >
        <Box>
          <Flex align="center" gap="0.7rem" mb="0.4rem">
            <Heading as="h3" fontFamily="heading" fontSize="1.3rem" fontWeight="400" color="brand.ink">
              Site Mode
            </Heading>
            {mode && (
              <Box
                px="0.7rem"
                py="0.15rem"
                borderRadius="20px"
                fontSize="0.62rem"
                fontWeight="600"
                letterSpacing="0.15em"
                textTransform="uppercase"
                bg={mode === 'live' ? 'brand.wash' : '#FBEDEB'}
                color={mode === 'live' ? 'brand.mid' : 'brand.terracotta'}
              >
                {mode}
              </Box>
            )}
          </Flex>
          <Text fontSize="0.85rem" color="ink.soft" maxW="520px">
            {mode === 'live'
              ? 'The site is open to everyone. Only the portal requires login.'
              : 'The site requires a Microsoft sign-in to view. Switch to Live to open it to the public.'}
          </Text>
        </Box>
        <Flex gap="0.6rem" flexShrink="0">
          {(['locked', 'live'] as SiteMode[]).map((option) => (
            <chakra.button
              key={option}
              onClick={() => void changeMode(option)}
              disabled={switching || mode === null}
              bg={mode === option ? 'brand.ink' : 'paper.white'}
              color={mode === option ? 'paper.white' : 'ink.body'}
              border="1px solid"
              borderColor={mode === option ? 'brand.ink' : 'paper.border'}
              fontSize="0.72rem"
              fontWeight="500"
              letterSpacing="0.12em"
              textTransform="uppercase"
              px="1.6rem"
              py="0.7rem"
              cursor="pointer"
              borderRadius="20px"
              opacity={switching ? 0.6 : 1}
              transition="all 0.2s"
              _hover={mode === option ? {} : { borderColor: 'brand.mid', color: 'brand.mid' }}
            >
              {option === 'locked' ? '🔒 Locked' : '🌐 Live'}
            </chakra.button>
          ))}
        </Flex>
      </Flex>

      {/* ── Inquiry notifications ─────────────────────────────────── */}
      <Box
        bg="paper.white"
        border="1px solid"
        borderColor={notificationsUnreachable ? 'brand.terracotta' : 'paper.border'}
        borderRadius="16px"
        p="1.8rem"
        mb="1.5rem"
      >
        <Flex align="center" gap="0.7rem" mb="0.4rem">
          <Heading as="h3" fontFamily="heading" fontSize="1.3rem" fontWeight="400" color="brand.ink">
            Inquiry Notifications
          </Heading>
          <Box
            px="0.7rem"
            py="0.15rem"
            borderRadius="20px"
            fontSize="0.62rem"
            fontWeight="600"
            letterSpacing="0.15em"
            textTransform="uppercase"
            bg={notificationsUnreachable ? '#FBEDEB' : notifications.enabled ? 'brand.wash' : 'paper.warm'}
            color={notificationsUnreachable ? 'brand.terracotta' : notifications.enabled ? 'brand.mid' : 'ink.muted'}
          >
            {notificationsUnreachable ? 'No recipient' : notifications.enabled ? 'On' : 'Off'}
          </Box>
        </Flex>

        {notificationsUnreachable && (
          <Box
            bg="#FBEDEB"
            border="1px solid"
            borderColor="brand.terracotta"
            borderRadius="10px"
            px="1.1rem"
            py="0.8rem"
            mb="1rem"
          >
            <Text fontSize="0.82rem" color="brand.terracotta">
              No one is being emailed. Inquiries are still saved to the Inquiries inbox, but nobody
              is told when one arrives — add an address below.
            </Text>
          </Box>
        )}
        <Text fontSize="0.85rem" color="ink.soft" mb="1.4rem" maxW="620px">
          Who gets emailed when someone sends the contact form. Every inquiry is saved to the{' '}
          <strong>Inquiries</strong> inbox either way — switching notifications off only stops the
          email, it never drops a message.
        </Text>

        <Grid templateColumns={{ base: '1fr', md: '2fr 1fr' }} gap="1.2rem" mb="1.4rem">
          <Box>
            <Text fontSize="0.66rem" fontWeight="600" letterSpacing="0.14em" textTransform="uppercase" color="brand.ink" mb="0.35rem">
              Recipients
            </Text>
            <Input
              type="text"
              value={recipientsText}
              onChange={(event) => setRecipientsText(event.target.value)}
              placeholder="denise@augustaexecsolutions.com, assistant@example.com"
              autoComplete="off"
              {...inputStyles}
              w="100%"
            />
            <Text fontSize="0.7rem" color="ink.muted" mt="0.35rem">
              Comma-separated. Replies go straight back to whoever filled in the form.
            </Text>
          </Box>
          <Box>
            <Text fontSize="0.66rem" fontWeight="600" letterSpacing="0.14em" textTransform="uppercase" color="brand.ink" mb="0.35rem">
              Subject Prefix
            </Text>
            <Input
              type="text"
              value={notifications.subjectPrefix}
              onChange={(event) =>
                setNotifications({ ...notifications, subjectPrefix: event.target.value })
              }
              placeholder="New inquiry"
              {...inputStyles}
              w="100%"
            />
          </Box>
        </Grid>

        <Flex justify="space-between" align="center" wrap="wrap" gap="1rem">
          <Flex align="center" gap="0.6rem">
            <chakra.button
              onClick={() => setNotifications({ ...notifications, enabled: !notifications.enabled })}
              bg={notifications.enabled ? 'brand.mid' : 'paper.border'}
              border="none"
              w="42px"
              h="24px"
              borderRadius="12px"
              cursor="pointer"
              position="relative"
              transition="background 0.2s"
              aria-pressed={notifications.enabled}
              aria-label="Toggle inquiry notification emails"
            >
              <Box
                position="absolute"
                top="3px"
                left={notifications.enabled ? '21px' : '3px'}
                w="18px"
                h="18px"
                borderRadius="50%"
                bg="paper.white"
                transition="left 0.2s"
              />
            </chakra.button>
            <Text fontSize="0.8rem" color="ink.body">
              Email me when an inquiry arrives
            </Text>
          </Flex>
          <chakra.button
            onClick={() => void saveNotifications()}
            disabled={savingNotifications}
            bg="brand.mid"
            color="paper.white"
            border="none"
            fontSize="0.72rem"
            fontWeight="500"
            letterSpacing="0.15em"
            textTransform="uppercase"
            px="2.2rem"
            py="0.8rem"
            borderRadius="24px"
            cursor="pointer"
            opacity={savingNotifications ? 0.6 : 1}
            _hover={{ bg: 'brand.ink' }}
          >
            {savingNotifications ? 'Saving…' : 'Save Notifications'}
          </chakra.button>
        </Flex>
      </Box>

      {/* ── LLM / AI assist ───────────────────────────────────────── */}
      <Box bg="paper.white" border="1px solid" borderColor="paper.border" borderRadius="16px" p="1.8rem">
        <Heading as="h3" fontFamily="heading" fontSize="1.3rem" fontWeight="400" color="brand.ink" mb="0.4rem">
          AI Assist — Claude
        </Heading>
        <Text fontSize="0.85rem" color="ink.soft" mb="1.4rem" maxW="620px">
          Powers the ✨ Enhance buttons across the portal. The API key is stored in the private
          portal storage container — it is never publicly readable and never ships in the site bundle.
        </Text>

        <Grid templateColumns={{ base: '1fr', md: '2fr 1fr' }} gap="1.2rem" mb="1.4rem">
          <Box>
            <Text fontSize="0.66rem" fontWeight="600" letterSpacing="0.14em" textTransform="uppercase" color="brand.ink" mb="0.35rem">
              Anthropic API Key
            </Text>
            <Input
              type="password"
              value={settings.llm.apiKey}
              onChange={(event) =>
                setSettings({ ...settings, llm: { ...settings.llm, apiKey: event.target.value } })
              }
              placeholder="sk-ant-…"
              autoComplete="off"
              {...inputStyles}
              w="100%"
            />
          </Box>
          <Box>
            <Text fontSize="0.66rem" fontWeight="600" letterSpacing="0.14em" textTransform="uppercase" color="brand.ink" mb="0.35rem">
              Model
            </Text>
            <chakra.select
              value={settings.llm.model}
              onChange={(event) =>
                setSettings({ ...settings, llm: { ...settings.llm, model: event.target.value } })
              }
              {...inputStyles}
              w="100%"
              h="41px"
            >
              {CLAUDE_MODELS.map((model) => (
                <option key={model.id} value={model.id}>
                  {model.label}
                </option>
              ))}
            </chakra.select>
          </Box>
        </Grid>

        <Flex justify="space-between" align="center" wrap="wrap" gap="1rem">
          <Text fontSize="0.72rem" color="ink.muted">
            Provider: Anthropic ·{' '}
            {settings.updatedAt !== defaultPortalSettings.updatedAt
              ? `last saved ${new Date(settings.updatedAt).toLocaleString()}`
              : 'not saved yet'}
          </Text>
          <chakra.button
            onClick={() => void saveLlm()}
            disabled={savingLlm}
            bg="brand.mid"
            color="paper.white"
            border="none"
            fontSize="0.72rem"
            fontWeight="500"
            letterSpacing="0.15em"
            textTransform="uppercase"
            px="2.2rem"
            py="0.8rem"
            borderRadius="24px"
            cursor="pointer"
            opacity={savingLlm ? 0.6 : 1}
            _hover={{ bg: 'brand.ink' }}
          >
            {savingLlm ? 'Saving…' : 'Save LLM Settings'}
          </chakra.button>
        </Flex>
      </Box>
    </Box>
  );
}
